/**
 * Notification Service V2
 * 
 * Implementation matching the NOTIFICATIONS_ANNOUNCEMENTS_PLAN.md specification
 * Handles role-based notifications, manager announcements, and browser push notifications
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { AppNotification, NotificationData, NotificationFilters } from '../@types/notification';

export class NotificationServiceV2 {
  private static readonly NOTIFICATIONS_SUBCOLLECTION = 'notifications';
  private static readonly NOTE_HISTORY_SUBCOLLECTION = 'notesHistory';

  /**
   * Create a notification
   */
  static async createNotification(data: NotificationData): Promise<string> {
    try {
      const notificationRef = await addDoc(
        collection(db, 'users', data.userId, this.NOTIFICATIONS_SUBCOLLECTION),
        {
        userId: data.userId,
        type: data.type,
        title: data.title,
        message: data.message,
        priority: data.priority || 'MEDIUM',
        read: false,
        createdAt: Timestamp.now(),
        metadata: data.metadata || {},
        pushSent: false
        }
      );

      return notificationRef.id;
    } catch (error: any) {
      console.error('Error creating notification:', error);
      throw new Error(`Failed to create notification: ${error.message}`);
    }
  }

  /**
   * Get user notifications with optional filters
   */
  static async getUserNotifications(
    userId: string, 
    filters?: NotificationFilters
  ): Promise<AppNotification[]> {
    try {
      const constraints: any[] = [where('userId', '==', userId)];

      if (filters?.type) {
        constraints.push(where('type', '==', filters.type));
      }

      if (filters?.read !== undefined) {
        constraints.push(where('read', '==', filters.read));
      }

      if (filters?.priority) {
        constraints.push(where('priority', '==', filters.priority));
      }

      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(50));

      const q = query(
        collection(db, 'users', userId, this.NOTIFICATIONS_SUBCOLLECTION),
        ...constraints
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AppNotification));
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      throw new Error(`Failed to fetch notifications: ${error.message}`);
    }
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string, userId: string): Promise<void> {
    try {
      const notificationRef = doc(
        db,
        'users',
        userId,
        this.NOTIFICATIONS_SUBCOLLECTION,
        notificationId
      );
      const notificationSnap = await getDoc(notificationRef);

      if (!notificationSnap.exists()) {
        throw new Error('Notification not found');
      }

      const notificationData = notificationSnap.data() as AppNotification;
      if (notificationData.userId !== userId) {
        throw new Error('Unauthorized: Cannot mark notification as read');
      }

      await updateDoc(notificationRef, {
        read: true,
        readAt: Timestamp.now()
      });
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      throw new Error(`Failed to mark notification as read: ${error.message}`);
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  static async markAllAsRead(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'users', userId, this.NOTIFICATIONS_SUBCOLLECTION),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);

      querySnapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          read: true,
          readAt: Timestamp.now()
        });
      });

      await batch.commit();
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
      throw new Error(`Failed to mark all notifications as read: ${error.message}`);
    }
  }

  /**
   * Delete notification
   */
  static async deleteNotification(notificationId: string, userId: string): Promise<void> {
    try {
      const notificationRef = doc(
        db,
        'users',
        userId,
        this.NOTIFICATIONS_SUBCOLLECTION,
        notificationId
      );
      const notificationSnap = await getDoc(notificationRef);

      if (!notificationSnap.exists()) {
        throw new Error('Notification not found');
      }

      const notificationData = notificationSnap.data() as AppNotification;
      if (notificationData.userId !== userId) {
        throw new Error('Unauthorized: Cannot delete notification');
      }

      await deleteDoc(notificationRef);
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      throw new Error(`Failed to delete notification: ${error.message}`);
    }
  }

  /**
   * Get unread count for a user
   */
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const q = query(
        collection(db, 'users', userId, this.NOTIFICATIONS_SUBCOLLECTION),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error: any) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Subscribe to real-time notifications for a user
   */
  static subscribeToNotifications(
    userId: string, 
    callback: (notification: AppNotification) => void
  ): Unsubscribe {
    const q = query(
        collection(db, 'users', userId, this.NOTIFICATIONS_SUBCOLLECTION),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added' || change.type === 'modified') {
            const notification = {
              id: change.doc.id,
              ...change.doc.data()
            } as AppNotification;
            callback(notification);
          }
        });
      },
      (error) => {
        console.error('Error in notification subscription:', error);
        // Don't throw - this allows the app to continue functioning
        // The error might be due to missing index or network issues
        if (error.code === 'failed-precondition') {
          console.warn('Firestore index required. Please create a composite index on notifications collection: userId (ascending), createdAt (descending)');
        }
      }
    );
  }

  /**
   * Track note submission history
   */
  static async trackNoteSubmission(
    userId: string,
    storeId: string,
    noteId: string
  ): Promise<void> {
    try {
      await addDoc(collection(db, 'Stores', storeId, this.NOTE_HISTORY_SUBCOLLECTION), {
        userId,
        storeId,
        noteId,
        submittedAt: Timestamp.now()
      });
    } catch (error: any) {
      console.error('Error tracking note submission:', error);
      // Don't throw - this is a tracking operation
    }
  }

  /**
   * Get users who have submitted notes for a store
   */
  static async getUsersWithNoteHistory(storeId: string): Promise<string[]> {
    try {
      const q = collection(db, 'Stores', storeId, this.NOTE_HISTORY_SUBCOLLECTION);

      const querySnapshot = await getDocs(q);
      const userIds = new Set<string>();

      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.userId) {
          userIds.add(data.userId);
        }
      });

      return Array.from(userIds);
    } catch (error: any) {
      console.error('Error getting users with note history:', error);
      return [];
    }
  }
}

